/**
 * Fotos de los agentes — GEN-AG-006. La FOTO en vigor de cada agente sale de su
 * ficha: el descriptor trae en `agents_visibles[].avatar_url` la ruta del kernel
 * (`/v1/agentes/<id>/imagen/<foto>`, o "" si no tiene). La imagen está tras la
 * puerta del kernel, así que se pide con el token y se pinta como blob URL.
 *
 * GEN-PX-010: el agente trae además `foto_url` (la FOTO completa, sin pasar por
 * la miniatura). `avatar_url` sigue siendo miniatura-si-hay (barra lateral y
 * avatares pequeños); las tarjetas grandes de Inicio usan `useFotoGrandeAgente`
 * (foto_url || avatar_url: con un kernel sin `foto_url` se ve lo de siempre).
 *
 * Una sola carga por RUTA (si foto_url == avatar_url no se pide dos veces) para toda la app (barra lateral, cabecera del chat,
 * «En este chat»…). Fuera del provider, o sin foto, `fotoDe` devuelve null y
 * AgentAvatar cae a las iniciales.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { getImagenKernel } from '../api/client.js'

const FotosAgentesContext = createContext(() => null)
const FotoGrandeContext = createContext(() => null)

export function FotosAgentesProvider({ agents = [], children }) {
  // blob URL por RUTA del kernel (no por agente): evita pedir dos veces la misma imagen.
  const [porRuta, setPorRuta] = useState({})
  // Clave estable: solo se recarga si cambia la imagen en vigor de algún agente.
  const clave = agents.map(a => `${a.id}=${a.avatar_url || ''}|${a.foto_url || ''}`).join(';')

  useEffect(() => {
    let vivo = true
    const urls = []
    setPorRuta({})
    const rutas = new Set(agents.flatMap(a => [a.avatar_url, a.foto_url]).filter(Boolean))
    for (const ruta of rutas) {
      getImagenKernel(ruta)
        .then(blob => {
          if (!vivo || !blob) return
          const url = URL.createObjectURL(blob)
          urls.push(url)
          setPorRuta(prev => ({ ...prev, [ruta]: url }))
        })
        .catch(() => {}) // sin foto: iniciales
    }
    return () => {
      vivo = false
      urls.forEach(u => URL.revokeObjectURL(u))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave])

  const porId = useMemo(() => new Map(agents.map(a => [a.id, a])), [clave])

  const fotoDe = useMemo(
    () => agent => {
      const a = typeof agent === 'string' ? porId.get(agent) : agent
      return (a?.avatar_url && porRuta[a.avatar_url]) || null
    },
    [porRuta, porId],
  )

  // Foto completa. Con `foto_url` solo vale ella (mientras carga, iniciales: no se
  // enseña un instante la miniatura recortada); sin `foto_url` (kernel anterior), avatar_url.
  const fotoGrandeDe = useMemo(
    () => agent => {
      const a = typeof agent === 'string' ? porId.get(agent) : agent
      const ruta = a?.foto_url || a?.avatar_url
      return (ruta && porRuta[ruta]) || null
    },
    [porRuta, porId],
  )

  return (
    <FotosAgentesContext.Provider value={fotoDe}>
      <FotoGrandeContext.Provider value={fotoGrandeDe}>{children}</FotoGrandeContext.Provider>
    </FotosAgentesContext.Provider>
  )
}

// fotoDe(agent | id) -> blob URL de la imagen en vigor (miniatura si hay), o null.
export function useFotoAgente() {
  return useContext(FotosAgentesContext)
}

// fotoGrandeDe(agent | id) -> blob URL de la FOTO completa de la ficha, o null.
export function useFotoGrandeAgente() {
  return useContext(FotoGrandeContext)
}
